import React from "react";
import { motion } from "framer-motion";
import { useT } from "@/i18n/LanguageContext";

// Mamm "Meet the Family" — a playful 4-character grid. KIKA and BOGO have
// real bottle photos; BAMBAM and VAK don't have pack shots yet, so they
// render as vivid character cards with a big animal emoji-glyph and character
// palette until the photography lands. Cards wiggle subtly on hover — kids'
// brand energy without breaking the site's editorial calm.
// -----------------------------------------------------------------------------
const SUN = "#FFD53F";
const INK = "#1A0A00";

const CHARACTERS = [
  {
    key: "kika",
    image: "/products/mamm-kika.webp",
    bg: "#C4E9F5",       // soft sky-blue
    accent: "#2C8FD9",   // blue
    ink: "#0F3541",
    ribbon: "#1E6EA3",
  },
  {
    key: "bogo",
    image: "/products/mamm-bogo.webp",
    bg: "#FDD3D3",       // soft pink
    accent: "#E4004E",   // red
    ink: "#5B0B25",
    ribbon: "#C41947",
  },
  {
    key: "bambam",
    image: null,          // no pack shot yet — typographic card
    bg: "#DFF0C4",       // soft mint
    accent: "#4A8A1F",   // green
    ink: "#1F3A0C",
    ribbon: "#3A6E17",
    glyph: "◐",           // dog-ear silhouette placeholder
    letterInitial: "B",
  },
  {
    key: "vak",
    image: null,
    bg: "#FFEEC0",       // soft cream-yellow
    accent: "#D89512",   // amber
    ink: "#3A2807",
    ribbon: "#A66C08",
    glyph: "◕",
    letterInitial: "V",
  },
];

const FALLBACK = {
  kika: {
    name: "Kika",
    animal: "The Monkey",
    body: "Cheeky, curious, always in the mood for adventure. Kika brings the strawberry-forward brightness.",
  },
  bogo: {
    name: "Bogo",
    animal: "The Elephant",
    body: "Big-hearted, gentle, and the calm one of the crew. Bogo carries the plum-apple depth.",
  },
  bambam: {
    name: "Bambam",
    animal: "The Dog",
    body: "Loyal, playful, and always ready for another round. Bambam brings a fresh apple-strawberry lift.",
  },
  vak: {
    name: "Vak",
    animal: "The Duck",
    body: "Splashy, sunny and a little cheeky. Vak's the mixed-fruit party in a bottle.",
  },
};

function CharacterCard({ character, index }) {
  const { t } = useT();
  const isTranslated = (v) => typeof v === "string" && !v.startsWith("brandContent.");
  const fb = FALLBACK[character.key];
  const nameRaw = t(`brandContent.mamm.family.${character.key}.name`);
  const animalRaw = t(`brandContent.mamm.family.${character.key}.animal`);
  const bodyRaw = t(`brandContent.mamm.family.${character.key}.body`);
  const name = isTranslated(nameRaw) ? nameRaw : fb.name;
  const animal = isTranslated(animalRaw) ? animalRaw : fb.animal;
  const body = isTranslated(bodyRaw) ? bodyRaw : fb.body;

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.08 }}
      whileHover={{ rotate: index % 2 === 0 ? -1.2 : 1.2, y: -4 }}
      className="relative overflow-hidden rounded-3xl flex flex-col shadow-[0_20px_45px_-20px_rgba(0,0,0,0.35)] cursor-pointer"
      style={{ background: character.bg, color: character.ink }}
      data-testid={`mamm-family-${character.key}`}
    >
      {/* Character bottle or typographic glyph */}
      {character.image ? (
        <div className="relative flex items-end justify-center pt-10 pb-4 px-6 min-h-[360px]">
          <img
            src={character.image}
            alt={`${name} — ${animal}`}
            className="h-[360px] w-auto object-contain drop-shadow-[0_20px_35px_rgba(0,0,0,0.15)]"
          />
        </div>
      ) : (
        <div
          className="relative flex flex-col items-center justify-center pt-14 pb-4 px-6 min-h-[360px]"
        >
          {/* Big playful letter — placeholder until real bottle photo arrives */}
          <div
            className="font-editorial italic leading-none"
            style={{ color: character.accent, fontSize: "clamp(9rem, 15vw, 13rem)" }}
          >
            {character.letterInitial}
          </div>
          <span
            className="mt-1 text-xs font-bold uppercase"
            style={{ color: character.ribbon, letterSpacing: "0.32em" }}
          >
            {animal}
          </span>
        </div>
      )}

      <div className="relative px-7 pb-8 pt-2">
        <span
          className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[0.6rem] font-bold uppercase tracking-[0.28em]"
          style={{ background: character.ribbon, color: character.bg }}
        >
          {animal}
        </span>

        <h3
          className="mt-4 font-editorial italic leading-[1.05] text-3xl md:text-4xl"
          style={{ color: character.ink }}
        >
          {name}
        </h3>

        <div
          className="mt-3 h-px w-14"
          style={{ background: `linear-gradient(90deg, ${character.accent}, transparent)` }}
        />

        <p
          className="mt-3 text-sm md:text-[15px] leading-[1.6]"
          style={{ color: `${character.ink}cc` }}
        >
          {body}
        </p>
      </div>
    </motion.article>
  );
}

export default function MammFamily() {
  const { t } = useT();
  const isTranslated = (v) => typeof v === "string" && !v.startsWith("brandContent.");
  const overlineRaw = t("brandContent.mamm.family.overline");
  const overline = isTranslated(overlineRaw) ? overlineRaw : "Meet the family";
  const titleRaw = t("brandContent.mamm.family.title");
  const title = isTranslated(titleRaw)
    ? titleRaw
    : "Four friends. One healthy juice.";

  return (
    <section
      className="relative overflow-hidden"
      style={{ background: "#FFF8DC", color: INK }}
      data-testid="mamm-family"
    >
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28">
        <div className="max-w-2xl mb-14">
          <p
            className="text-[0.7rem] md:text-xs font-bold uppercase mb-4"
            style={{ color: "#E4004E", letterSpacing: "0.36em" }}
          >
            {overline}
          </p>
          <h2
            className="font-editorial italic leading-[1.05] text-[clamp(2.25rem,4.4vw,3.75rem)]"
            style={{ color: INK }}
          >
            {title}
          </h2>
          <div
            className="mt-6 h-px w-24"
            style={{ background: `linear-gradient(90deg, #E4004E, transparent)` }}
          />
        </div>

        <div className="grid gap-5 md:gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {CHARACTERS.map((c, i) => (
            <CharacterCard key={c.key} character={c} index={i} />
          ))}
        </div>
      </div>

      <div
        className="h-[3px] w-full"
        style={{
          background: `linear-gradient(90deg, transparent 5%, ${SUN} 20%, ${SUN} 80%, transparent 95%)`,
        }}
      />
    </section>
  );
}
